import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import { categories } from '@/mocks/categories';
import { canonicalUrl } from '@/utils/helpers';

const pillars = [
  { icon: 'ri-restart-line', tint: 'bg-primary-50 text-primary-700', title: '事件还原', desc: '先搞清楚"发生了什么"，再把同一事件的多个版本拼起来，尽量还原完整语境，避免被单段片段带偏。' },
  { icon: 'ri-eye-line', tint: 'bg-accent-100 text-accent-800', title: '反转追踪', desc: '热点从不静止。我们持续跟进瓜中瓜的每一个新节点，记录当事人每一次回应的前后差异，做一台"反转记录仪"。' },
  { icon: 'ri-list-check-3', tint: 'bg-secondary-100 text-secondary-800', title: '证据梳理', desc: '录音、聊天记录、官方声明……关键证据按时间与来源归类，帮助你在最短时间内判断谁的说法更有可信度。' },
];

const principles = [
  { n: '01', title: '素材公开透明', text: '内容主要整理自全网公开讨论、当事人回应与媒体报道，不编造事实、不虚构来源。' },
  { n: '02', title: '提供线索而非结论', text: '我们负责把事件脉络和证据整理清楚，把判断权交还给读者，不替任何一方下结论。' },
  { n: '03', title: '尊重隐私与边界', text: '涉及个人隐私的敏感材料会做必要脱敏，绝不为了热度而消费无辜者。' },
  { n: '04', title: '保持追踪到尘埃落定', text: '反转不结束，更新不停更。每一个瓜我们都尽可能追到大结局或权威回应为止。' },
];

const aboutImage =
  'https://readdy.ai/api/search-image?query=cozy%20editorial%20workspace%20with%20open%20notebooks%20and%20newspaper%20clippings%20and%20magnifying%20glass%20on%20warm%20desk%2C%20soft%20morning%20light%2C%20research%20timeline%20board%20in%20blurred%20background%2C%20amber%20and%20cream%20palette%2C%20cinematic%20photography&width=820&height=620&seq=about-hero-01&orientation=landscape';

export default function AboutPage() {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于每日大瓜',
      url: canonicalUrl(),
      inLanguage: 'zh-CN',
      about: { '@type': 'Organization', name: '每日大瓜', description: '全网吃瓜时间线资讯站' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '每日大瓜',
      url: window.location.origin,
      description: '全网吃瓜时间线，瓜中瓜连环反转全程追踪。',
      contactPoint: [{ '@type': 'ContactPoint', contactType: 'feedback', email: 'hello@example.com' }],
    },
  ];

  return (
    <>
      <Seo
        title="关于每日大瓜 | 我们的定位、理念与内容原则"
        description="了解每日大瓜：一个专注全网吃瓜时间线的娱乐资讯站。我们以事件还原、反转追踪、证据梳理为方法，坚持素材透明、不站队、尊重隐私的内容原则。"
        keywords="关于每日大瓜,全网吃瓜时间线,内容原则,网站定位,联系我们"
        jsonLd={schema}
      />
      <SiteHeader />

      <main>
        <section className="mx-auto w-full max-w-6xl px-4 md:px-6 py-10 md:py-14">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center">
            <div className="flex-1">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
                <i className="ri-question-answer-line" aria-hidden="true" /> 关于我们
              </span>
              <h1 className="mt-3 text-2xl md:text-4xl font-bold leading-tight">关于每日大瓜</h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                每日大瓜是一档面向中文互联网用户的娱乐资讯站，专注"全网吃瓜时间线"。面对铺天盖地的反转、录音与截图，
                我们想做的其实很简单——帮你把每一场大戏的前因后果、关键节点和可信证据，用一条清晰的时间线串起来。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                我们不靠标题博眼球，也不站队表态，只做"追瓜路上的搬运工与整理员"，把素材摆在你面前，把判断留给你。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/" className="inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600">
                  <i className="ri-fire-line mr-1.5" aria-hidden="true" /> 回首页吃瓜
                </Link>
                <a href="mailto:hello@example.com" className="inline-flex items-center whitespace-nowrap rounded-md border border-background-200 px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:border-primary-300 hover:text-primary-700">
                  <i className="ri-mail-line mr-1.5" aria-hidden="true" /> 联系我们
                </a>
              </div>
            </div>
            <div className="w-full lg:w-[420px]">
              <img src={aboutImage} alt="每日大瓜编辑部整理吃瓜时间线的工作台" className="w-full rounded-lg object-cover" />
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14">
          <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
            <div className="mb-6 max-w-2xl">
              <span className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700">
                <i className="ri-compass-3-line" aria-hidden="true" /> 我们的方法
              </span>
              <h2 className="text-xl md:text-2xl font-bold">一套方法，三条主线</h2>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {pillars.map((p) => (
                <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className={`mb-3 flex h-11 w-11 items-center justify-center rounded-md text-xl ${p.tint}`}>
                    <i className={p.icon} aria-hidden="true" />
                  </span>
                  <h3 className="text-base font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 md:px-6 py-10 md:py-14">
          <div className="mb-6 max-w-2xl">
            <span className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
              <i className="ri-shield-check-line" aria-hidden="true" /> 内容原则
            </span>
            <h2 className="text-xl md:text-2xl font-bold">我们坚持这样吃瓜</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {principles.map((pr) => (
              <div key={pr.n} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="font-label text-3xl font-extrabold text-accent-300">{pr.n}</span>
                <div>
                  <h3 className="text-base font-bold text-foreground-950">{pr.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{pr.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 md:px-6 pb-12">
          <div className="rounded-lg border border-background-200 bg-secondary-50/60 p-6 md:p-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-bold">从栏目开始，找到你想吃的那颗瓜</h2>
                <p className="mt-2 text-sm text-foreground-600">影视、音乐、综艺、圈内动态，四大栏目覆盖娱乐圈各大瓜田。</p>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className={`${c.icon} text-primary-600`} aria-hidden="true" /> {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  );
}